/**
 * The garment behind a locked Mum Hub piece (MAR-216, MAR-219).
 *
 * ## Why this exists
 *
 * A locked tile has no photograph. The API sends `image: null` by
 * construction, which is the only version of a paywall a screenshot of the
 * network tab cannot defeat, and it left the preview looking like a grid of
 * empty washes. Adrian's note, against a screenshot of the capsule grid: the
 * locked items look plain, make it look as though there is something behind
 * them. So the backend now says WHAT KIND of thing is behind each one, and
 * this file is the drawing of each kind.
 *
 * ## Why the paths live in one module
 *
 * They are drawn twice: react-native-svg in the app, inline `<svg>` on the
 * web. Two copies of the same path data drift silently, because nothing about
 * a wrong `d` string throws, renders red or fails a type check. It draws a
 * slightly different trouser leg on one surface and nobody notices for
 * months. One string, imported by both, cannot drift.
 *
 * ## What they may and may not be
 *
 * Every one is a SINGLE filled shape in a `0 0 100 130` box, flat-lay, with
 * no seam, no button, no logo and no print. That restraint is not only taste:
 * the silhouette sits on the paid side of a paywall, and any detail specific
 * enough to identify a garment would give away the thing being sold. A
 * trouser leg is a trouser leg; it is not Maria's trousers.
 *
 * They are drawn in curves rather than in the straight lines of an icon set,
 * because they are shown at low opacity behind a soft wash on a British site
 * whose type is Playfair. A crisp pictogram would read as an error state.
 *
 * ## Where this file is
 *
 * `packages/cch-shared`, not the repository's `shared/`. Only `shared/` is a
 * misleading name here: it is shared between `client/` and `server/`, and the
 * app cannot import from it at all (`mobile/` is not a workspace member and
 * resolves cross-package code through `@cch/shared`). A path list the app
 * cannot import would have to be copied into the app, which is the exact
 * drift this module exists to prevent.
 */

/**
 * The kinds the backoffice can send.
 *
 * Deliberately coarse. The list is a drawing vocabulary and not a taxonomy:
 * anything finer would mean a path per garment, and a garment-accurate
 * outline behind a paywall is the leak this whole feature is careful about.
 */
export const MUM_HUB_SILHOUETTE_KINDS = [
  'top',
  'shirt',
  'knit',
  'dress',
  'trousers',
  'skirt',
  'jacket',
  'shoes',
  'bag',
  'accessory',
  'jewellery',
  'underwear',
  'nightwear',
  'baby',
  'other',
] as const;

export type MumHubSilhouette = (typeof MUM_HUB_SILHOUETTE_KINDS)[number];

/** The box every path is drawn in. Portrait, matching a 3:4 garment tile. */
export const MUM_HUB_SILHOUETTE_VIEW_BOX = '0 0 100 130';

/**
 * One path per kind.
 *
 * A `Record` over the union rather than a lookup with a default, so adding a
 * word to {@link MUM_HUB_SILHOUETTE_KINDS} without drawing it is a type error
 * rather than a tile that quietly falls back to a hanger.
 */
export const MUM_HUB_SILHOUETTE_PATHS: Record<MumHubSilhouette, string> = {
  // A tee: soft shoulders, short set-in sleeves, a scooped neck.
  top: 'M36 22C40 30 60 30 64 22L82 30C88 33 92 40 94 50L80 58L78 100C78 104 75 106 71 106L29 106C25 106 22 104 22 100L20 58L6 50C8 40 12 33 18 30Z',
  // A shirt: the same body, plus the collar's V, which is the only thing that
  // tells a shirt from a tee at this size.
  shirt:
    'M38 20L50 32L62 20L80 28C86 31 90 38 92 50L79 57L77 101C77 105 74 107 70 107L30 107C26 107 23 105 23 101L21 57L8 50C10 38 14 31 20 28Z',
  // A jumper: everything a shade wider and longer, which at low opacity is
  // what knitwear looks like from across a room.
  knit: 'M35 24C41 31 59 31 65 24L84 32C90 35 95 44 96 56L82 62L80 104C80 108 77 110 73 110L27 110C23 110 20 108 20 104L18 62L4 56C5 44 10 35 16 32Z',
  // A dress: a fitted bodice that leaves the waist and opens into a hem.
  dress:
    'M37 20C42 28 58 28 63 20L78 27C84 30 88 37 90 46L78 52L76 66C82 84 88 100 92 112C93 115 91 118 87 118L13 118C9 118 7 115 8 112C12 100 18 84 24 66L22 52L10 46C12 37 16 30 22 27Z',
  // Trousers: a waistband and two legs with the crotch notched between them.
  trousers:
    'M32 22L68 22C71 22 73 25 73 29L69 117C69 120 67 121 64 121L60 121C57 121 56 120 56 117L50 64L44 117C44 120 43 121 40 121L36 121C33 121 31 120 31 117L27 29C27 25 29 22 32 22Z',
  // A skirt: a waistband and an A-line that widens all the way down.
  skirt:
    'M28 26C28 24 30 22 32 22L68 22C70 22 72 24 72 26L74 40C80 66 86 92 90 108C91 112 88 114 84 114L16 114C12 114 9 112 10 108C14 92 20 66 26 40Z',
  // A jacket: shirt geometry with a lapel deep enough to read as an open front
  // without cutting a hole in the shape.
  jacket:
    'M36 20L50 40L64 20L82 28C88 31 93 40 95 52L81 59L79 106C79 110 76 112 72 112L28 112C24 112 21 110 21 106L19 59L5 52C7 40 12 31 18 28Z',
  // A court shoe, in profile: a toe, a vamp, a topline that dips to the
  // instep, and a block heel with the arch lifted clear of the ground. The
  // heel is what tells it from a lump at 46px, and the reason it is not the
  // flat loafer this used to be, which filled a third of the box and read as
  // a smudge with a bite out of it.
  shoes:
    'M8 104L52 104C60 104 66 98 69 90L74 104L90 104C92 104 93 102 93 99L93 62C93 56 88 52 82 54C71 58 63 65 56 73C46 85 28 95 14 98C10 99 8 101 8 104Z',
  // A handbag: a body that tapers to its base and a top handle. The handle is
  // a second closed subpath, a crescent rather than a stroke, so the whole
  // thing stays one filled path with no stroke width to scale wrongly on a
  // 46px thumb. It was widened from six units to ten, because at the thumb
  // size the old one was under three pixels of blush and closed up into the
  // body. One bag stands for totes and handbags alike: the difference between
  // them is a proportion, and neither survives being told apart at 46px.
  bag: 'M22 48L78 48C82 48 84 51 84 55L88 108C88 113 85 116 80 116L20 116C15 116 12 113 12 108L16 55C16 51 18 48 22 48ZM34 48C34 34 40 25 50 25C60 25 66 34 66 48L56 48C56 39 54 33 50 33C46 33 44 39 44 48Z',
  // A scarf, hung: a loop over the neck and two tails of unequal length
  // falling from it, with a gap down the middle. The loop used to be a
  // crescent with both tails fused into one stem below it, which at any size
  // read as a mushroom rather than as anything anybody wears.
  accessory:
    'M28 42C28 30 38 22 50 22C62 22 72 30 72 42L75 114C75 118 72 120 68 120L58 120C54 120 51 118 51 114L58 42C58 36 55 32 50 32C45 32 42 36 42 42L40 92C40 96 37 98 33 98L26 98C22 98 19 96 19 92Z',
  // A necklace: a chain falling in a U from the collarbones, with a teardrop
  // pendant hanging at the low point. Two subpaths, the band and the drop,
  // which touch so the pendant reads as hung from the chain rather than as a
  // second object floating under it. Scarves, hats and belts stay under
  // `accessory`; this is for the fine things beside them.
  jewellery:
    'M16 28C16 64 30 94 50 94C70 94 84 64 84 28L76 28C76 58 65 86 50 86C35 86 24 58 24 28ZM50 88C55 96 61 97 61 105C61 111 56 116 50 116C44 116 39 111 39 105C39 97 45 96 50 88Z',
  // Briefs, and nothing else. The one kind where the restraint is the point:
  // this is drawn on a page she may open on a train.
  underwear:
    'M16 44L84 44C87 44 89 47 88 50C86 62 80 70 70 74C63 77 58 82 55 90C53 95 47 95 45 90C42 82 37 77 30 74C20 70 14 62 12 50C11 47 13 44 16 44Z',
  // Pyjamas: a loose top over a pair of bottoms, as two subpaths.
  nightwear:
    'M36 20C41 27 59 27 64 20L80 27C85 30 89 37 91 46L79 52L77 70C77 73 75 74 72 74L28 74C25 74 23 73 23 70L21 52L9 46C11 37 15 30 20 27ZM28 82L72 82C74 82 76 84 75 86L72 116C72 119 70 120 67 120L58 120C55 120 53 119 53 116L50 96L47 116C47 119 45 120 42 120L33 120C30 120 28 119 28 116L25 86C24 84 26 82 28 82Z',
  // A sleepsuit: one piece, arms and legs, with the feet left rounded.
  baby: 'M38 22C42 28 58 28 62 22L76 28C81 31 85 38 87 47L76 52L75 63C76 65 76 66 76 68L73 104C73 107 71 108 68 108L58 108C55 108 53 107 53 104L50 76L47 104C47 107 45 108 42 108L32 108C29 108 27 107 27 104L24 68C24 66 24 65 25 63L24 52L13 47C15 38 19 31 24 28Z',
  // A hanger. The honest answer to "something is here and we have not been
  // told what", and the reason `other` is a drawing rather than a blank.
  other:
    'M50 30C44 30 40 34 40 40C40 43 42 45 45 45C48 45 50 43 50 40C50 38 51 37 52 37C54 37 55 39 54 41L48 54L10 82C6 85 4 89 4 93C4 97 7 100 12 100L88 100C93 100 96 97 96 93C96 89 94 85 90 82L52 54L58 44C62 38 58 30 50 30Z',
};

const KINDS = new Set<string>(MUM_HUB_SILHOUETTE_KINDS);

/**
 * The kind a payload asked for, or `other`.
 *
 * Takes `unknown` because it is fed straight from JSON. A missing field is the
 * shape production answers with today, and a word this build has never heard
 * of is the shape it answers with the day the backoffice adds one: both are
 * `other`, because a hanger is a better locked tile than a crash or a hole.
 */
export function readSilhouette(value: unknown): MumHubSilhouette {
  return typeof value === 'string' && KINDS.has(value)
    ? (value as MumHubSilhouette)
    : 'other';
}

/** The path for a kind. Never undefined: unknown words become `other`. */
export function silhouettePath(kind: MumHubSilhouette): string {
  return MUM_HUB_SILHOUETTE_PATHS[kind];
}
